import type { PriceObservation } from '@recipes/contracts';
import { storePriceIndex } from '@recipes/domain';
import { Alert, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@recipes/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useCatalogBoot, useCatalogStores } from '@/features/catalog/hooks';
import { StoreLoyaltyCardSection } from '@/features/catalog/loyalty-cards';
import { StoreRatingCard } from '@/features/catalog/store-rating';
import {
  formatDay,
  formatMoney,
  formatMoneyDelta,
  priceHere,
  storeRefOf,
  today,
  useAllPrices,
  useCurrency,
  usePriceRules,
  usePricesEnabled,
} from '@/features/prices/hooks';
import { PriceChangeNote } from '@/features/prices/price-parts';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { useShoppingHistory } from '@/features/shopping/hooks';
import { offlineDb } from '@/lib/offline/db';

const WINDOWS = [3, 6, 12] as const;
const signed = (percent: string) =>
  percent.startsWith('-') ? `−${percent.slice(1)}` : `+${percent}`;

/**
 * "Your prices here" (docs/partial/shopping-in-store-spec.md 5.7, 6.8): how the household's own
 * basket changed at this store, the biggest increases and decreases with their evidence, and
 * every product with a price there. Computed from the device copy; with fewer comparable
 * products than the minimum it says so instead of showing a number.
 */
export function CatalogStorePricesPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  useCatalogBoot();
  const enabled = usePricesEnabled();
  const stores = useCatalogStores();
  const store = stores.find((candidate) => candidate.id === id) ?? null;
  const observations = useAllPrices();
  const history = useShoppingHistory();
  const currency = useCurrency();
  const rules = usePriceRules();
  const [months, setMonths] = useState<(typeof WINDOWS)[number]>(6);
  const products = useLiveQuery(() => offlineDb.catalogProducts.toArray(), []);
  const productName = useMemo(() => {
    const names = new Map(
      (products ?? []).map(({ product }) => [
        product.id,
        product.brand ? `${product.brand} ${product.name}` : product.name,
      ]),
    );
    return (productId: string | null) => names.get(productId ?? '') ?? t('prices.store.unknown');
  }, [products, t]);

  // A chain sees all its branches; a branch sees itself.
  const here = useMemo(
    () =>
      observations.filter(
        (o) =>
          o.currency === currency &&
          o.productId &&
          (o.storeId === id || (store && !store.parentStoreId && o.chainId === id)),
      ),
    [currency, id, observations, store],
  );
  const byProduct = useMemo(() => {
    const groups: Record<string, PriceObservation[]> = {};
    for (const observation of here) (groups[observation.productId!] ??= []).push(observation);
    return groups;
  }, [here]);
  // How often the household bought a product: the memory of the list knows it by name.
  const purchases = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const entry of history)
      if (entry.lastProductId)
        counts[entry.lastProductId] = (counts[entry.lastProductId] ?? 0) + entry.useCount;
    return counts;
  }, [history]);

  const comparable = useMemo(
    () =>
      storePriceIndex(byProduct, purchases, months * 30, {
        at: today(),
        minProducts: 1,
        minPercent: rules.minPercent,
      }),
    [byProduct, months, purchases, rules.minPercent],
  );
  const index = comparable && comparable.products >= rules.minProducts ? comparable : null;
  const current = useMemo(() => {
    if (!store) return [];
    return Object.entries(byProduct)
      .flatMap(([productId, group]) => {
        const price = priceHere(group, storeRefOf(store), currency, rules);
        return price ? [{ productId, ...price }] : [];
      })
      .sort((a, b) => productName(a.productId).localeCompare(productName(b.productId)));
  }, [byProduct, currency, productName, rules, store]);

  if (!store)
    return (
      <PageContainer>
        <Alert variant="destructive">{t('prices.store.notFound')}</Alert>
      </PageContainer>
    );

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={store.name}
        description={t('prices.store.title')}
        backTo="/catalog/stores"
      />
      <div className="mb-6 space-y-4">
        <StoreRatingCard store={store} />
        <StoreLoyaltyCardSection store={store} />
      </div>
      {!enabled && <Alert>{t('prices.store.disabled')}</Alert>}

      {enabled && (
        <div className="space-y-6">
          <section aria-labelledby="store-index-heading" data-testid="store-price-index">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 id="store-index-heading" className="text-lg font-semibold">
                {t('prices.store.indexTitle')}
              </h2>
              <Select
                value={String(months)}
                onValueChange={(value) => setMonths(Number(value) as (typeof WINDOWS)[number])}
              >
                <SelectTrigger
                  className="w-40"
                  aria-label={t('prices.store.window')}
                  data-testid="store-index-window"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WINDOWS.map((value) => (
                    <SelectItem key={value} value={String(value)}>
                      {t('prices.store.months', { count: value })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {index ? (
              <>
                <p className="text-2xl font-semibold tabular-nums" data-testid="store-index-value">
                  {signed(index.percent)} %
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('prices.store.indexBasis', { months, count: index.products })} ·{' '}
                  {t('prices.store.counts', {
                    up: index.up,
                    down: index.down,
                    unchanged: index.unchanged,
                  })}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{t('prices.store.indexNote')}</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="store-index-too-few">
                {t('prices.store.tooFew', {
                  have: comparable?.products ?? 0,
                  need: rules.minProducts,
                })}
              </p>
            )}
          </section>

          {index && index.top.length > 0 && (
            <section aria-labelledby="store-top-heading">
              <h2 id="store-top-heading" className="mb-2 text-lg font-semibold">
                {t('prices.store.topTitle')}
              </h2>
              <ul className="divide-y rounded-lg border text-sm" data-testid="store-top-changes">
                {index.top.slice(0, 10).map((entry) => (
                  <li key={entry.productId} className="p-3">
                    <Link
                      className="font-medium underline"
                      to={`/catalog/products/${entry.productId}`}
                    >
                      {productName(entry.productId)}
                    </Link>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {formatMoney(entry.start.price, currency)} (
                      {formatDay(entry.start.observedAt)}){' → '}
                      {formatMoney(entry.end.price, currency)} ({formatDay(entry.end.observedAt)}) ·{' '}
                      {entry.change!.basis === 'UNIT_PRICE'
                        ? t('prices.change.unitPrice', { percent: signed(entry.change!.percent) })
                        : `${formatMoneyDelta(entry.change!.absolute, currency)} (${signed(entry.change!.percent)} %)`}
                      {entry.weight > 1 &&
                        ` · ${t('prices.store.weight', { count: entry.weight })}`}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section aria-labelledby="store-products-heading">
            <h2 id="store-products-heading" className="mb-2 text-lg font-semibold">
              {t('prices.store.productsTitle', { count: current.length })}
            </h2>
            {current.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('prices.store.noProducts')}</p>
            ) : (
              <ul className="divide-y rounded-lg border text-sm" data-testid="store-products">
                {current.map((row) => (
                  <li
                    key={row.productId}
                    className="flex flex-wrap items-baseline justify-between gap-x-3 p-3"
                  >
                    <Link className="underline" to={`/catalog/products/${row.productId}`}>
                      {productName(row.productId)}
                    </Link>
                    <span className="tabular-nums">
                      {row.current.stale && `${t('prices.approx')} `}
                      {formatMoney(row.current.observation.price, currency)}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {formatDay(row.current.observation.observedAt)}
                      </span>
                    </span>
                    {row.change?.noticeable && (
                      <PriceChangeNote change={row.change} className="basis-full" />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </PageContainer>
  );
}
